// Fetch wrapper with JWT auth, transparent refresh on 401, and JSON helpers.
// Tokens live in the AuthStore; on refresh failure the store is cleared and a
// redirect to /login is triggered via window.location.hash.

import { auth } from './auth.js';

const BASE = 'https://portal.nestly.hu/api/v1';

let refreshPromise = null;

async function refreshTokens() {
  if (refreshPromise) return refreshPromise;
  const refresh = auth.refreshToken;
  if (!refresh) return null;
  refreshPromise = fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('refresh_failed');
      const data = await res.json();
      auth.setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
      return data.access_token;
    })
    .catch((err) => {
      auth.clear();
      window.location.hash = '#/login';
      throw err;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

async function rawRequest(path, { method = 'GET', body, headers = {}, retry = true, responseType = 'json' } = {}) {
  const finalHeaders = { Accept: responseType === 'blob' ? '*/*' : 'application/json', ...headers };
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth.accessToken) {
    finalHeaders['Authorization'] = `Bearer ${auth.accessToken}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : (body instanceof FormData ? body : JSON.stringify(body)),
  });

  if (res.status === 401 && retry && auth.refreshToken && !path.startsWith('/auth/refresh')) {
    try {
      await refreshTokens();
      return rawRequest(path, { method, body, headers, retry: false, responseType });
    } catch (_) {
      throw new ApiError('Unauthorized', 401, null);
    }
  }

  if (res.status === 204) return null;

  if (responseType === 'blob') {
    if (!res.ok) {
      const text = await res.text();
      const data = text ? safeJson(text) : null;
      const message = (data && (data.error?.message || data.message)) || res.statusText;
      throw new ApiError(message, res.status, data);
    }
    return await res.blob();
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const message = (data && (data.error?.message || data.message)) || res.statusText;
    throw new ApiError(message, res.status, data);
  }
  return data;
}

function safeJson(text) {
  try { return JSON.parse(text); } catch (_) { return null; }
}

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export const api = {
  get:    (path, opts) => rawRequest(path, { ...opts, method: 'GET' }),
  post:   (path, body, opts) => rawRequest(path, { ...opts, method: 'POST', body }),
  patch:  (path, body, opts) => rawRequest(path, { ...opts, method: 'PATCH', body }),
  put:    (path, body, opts) => rawRequest(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => rawRequest(path, { ...opts, method: 'DELETE' }),
};
